import { client } from './index';

export type SensorStatusLabel = 'reporting' | 'silent' | 'rejecting';

export interface SensorListItem {
  sensor_id: number;
  identifier: string;
  name: string | null;
  status: SensorStatusLabel;
  last_reading_at: string | null;
  last_value: Record<string, unknown> | null;
  asset_id: number | null;
  asset_name: string | null;
  asset_type_id: number | null;
  location_name: string | null;
  fed_meter_names: string[];
}

// Shared shape of the backend's `paginate` helper (Pagination concern).
export interface SensorsPagination {
  count: number;
  total: number;
  pages: number;
  page: number;
  from: number;
  to: number;
}

export interface SensorsListResponse {
  devices: SensorListItem[];
  pagination: SensorsPagination;
}

export interface SensorRejectedMessage {
  id: number;
  rejection_reason: string;
  received_at: string;
}

export interface SensorRecentReading {
  reading_at: string;
  reading_attributes: Record<string, unknown>;
  fed_meter: boolean;
}

export interface SensorDiagnostics {
  sensor_id: number;
  identifier: string;
  status_label: SensorStatusLabel;
  last_reading_at: string | null;
  last_rejected_message: SensorRejectedMessage | null;
  reporting_cadence_seconds: number | null;
  recent_readings: SensorRecentReading[];
}

export interface AccountCredentialStatus {
  exists: boolean;
  token_prefix: string | null;
  plain_token?: string;
}

export interface SensorMapAsset {
  id: number;
  name: string;
  latitude: string;
  longitude: string;
}

export interface SensorMapSensor extends SensorMapAsset {
  identifier: string;
  status_label: SensorStatusLabel;
  last_reported_at: string | null;
  reading_attributes: Record<string, unknown> | null;
}

export interface SensorMapResponse {
  assets: SensorMapAsset[];
  sensors: SensorMapSensor[];
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
}

export type SensorMessageOutcome = 'accepted' | 'rejected' | 'unknown_device';

export interface SensorMessageLogEntry {
  id: number;
  // Only as much of the body as the log's one-line cell can show. The whole
  // thing can be a megabyte, so it is fetched per entry via getMessageRawBody
  // when someone opens one, not shipped for all 25 rows of a page.
  raw_body_preview: string;
  status: 'accepted' | 'rejected';
  rejection_reason: string | null;
  received_at: string;
  outcome: SensorMessageOutcome;
  sensor_id: number | null;
  sensor_name: string | null;
  sensor_identifier: string | null;
  // The parsed payload as the backend stored it. XML arrives here already
  // converted to a nested hash, which is why the mapping form reads this
  // instead of trying to JSON.parse raw_body.
  reading_attributes: Record<string, unknown> | null;
  shape_hash: string | null;
  guessed_identifier: string | null;
  mapping: {
    id: number;
    identifier_field_path: string;
    date_field_path: string | null;
    date_format: SensorDateFormat | null;
    lat_field_path: string | null;
    lng_field_path: string | null;
  } | null;
}

// 'unmapped' are the entries still waiting to be mapped, 'mapped' the ones
// already feeding a sensor - the inbox shows one list of each.
export type SensorMessagesState = 'unmapped' | 'mapped';

export interface SensorMessagesResponse {
  messages: SensorMessageLogEntry[];
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
}

export type SensorDateFormat = 'iso8601' | 'epoch_seconds' | 'epoch_millis';

export interface CreateFormMappingRequest {
  shape_hash?: string;
  sensor_message_id?: number;
  identifier_field_path: string;
  date_field_path?: string;
  date_format?: SensorDateFormat;
  lat_field_path?: string;
  lng_field_path?: string;
  device_identifier_value?: string;
}

export interface FormMappingResponse {
  id: number;
  shape_hash: string;
  sensor_id: number | null;
}

export interface UpdateSensorRequest {
  name?: string;
  asset_id?: number | null;
  nestable_location_id?: number | null;
}

export interface UpdateSensorResponse {
  sensor_id: number;
  identifier: string;
  name: string | null;
  asset_id: number | null;
  nestable_location_id: number | null;
}

export interface CreateMeterMappingRequest {
  asset_id: number;
  meter_id: number;
  sensor_id: number;
  source_field_path: string;
}

export interface MeterMappingResponse {
  id: number;
  asset_id: number;
  meter_id: number;
}

// One page of sensors, never the whole account: every per-sensor lookup the
// endpoint does is proportional to how many sensors it is asked about.
// assetId scopes to one asset's sensors, which fit in a single page.
async function getMappedSensors(assetId?: number, page?: number) {
  const params = new URLSearchParams();
  if (assetId) params.set('asset_id', String(assetId));
  if (page) params.set('page', String(page));
  const query = params.toString() ? `?${params.toString()}` : '';
  const response = await client.get<SensorsListResponse>(`/api/sensors/device_tray/mapped${query}`);
  const { data } = response;
  return data;
}

async function getSensorDiagnostics(sensorId: number) {
  const response = await client.get<SensorDiagnostics>(`/api/sensors/diagnostics/${sensorId}`);
  const { data } = response;
  return data;
}

async function getAccountCredential() {
  const response = await client.get<AccountCredentialStatus>('/api/sensors/account_credential');
  const { data } = response;
  return data;
}

async function regenerateAccountCredential() {
  const response = await client.post<AccountCredentialStatus>('/api/sensors/account_credential');
  const { data } = response;
  return data;
}

// A page of the sensors with the most recent data, never the whole account:
// the endpoint is bounded and so is the number of markers the map creates.
async function getSensorsMap(page = 1) {
  const response = await client.get<SensorMapResponse>(`/api/sensors/map?page=${page}`);
  const { data } = response;
  return data;
}

// The full body of one entry, on demand. The log itself only carries previews.
async function getMessageRawBody(messageId: number) {
  const response = await client.get<{ id: number; raw_body: string }>(`/api/sensors/messages/${messageId}`);
  const { data } = response;
  return data;
}

async function getSensorMessages(page = 1, state?: SensorMessagesState) {
  const query = state ? `?page=${page}&state=${state}` : `?page=${page}`;
  const response = await client.get<SensorMessagesResponse>(`/api/sensors/messages${query}`);
  const { data } = response;
  return data;
}

// Only the totals: used to tell an account that has never received an entry
// apart from one that has, without downloading a page of payloads to find out.
async function getSensorMessagesCount() {
  const response = await client.get<SensorMessagesResponse>('/api/sensors/messages?count_only=true');
  const { data } = response;
  return data;
}

async function createFormMapping(request: CreateFormMappingRequest) {
  const response = await client.post<FormMappingResponse>('/api/sensors/form_mappings', request);
  const { data } = response;
  return data;
}

async function updateSensor(sensorId: number, request: UpdateSensorRequest) {
  const response = await client.patch<UpdateSensorResponse>(`/api/sensors/${sensorId}`, request);
  const { data } = response;
  return data;
}

// Soft delete: retires the sensor so it leaves every listing and its device's
// messages start being rejected. Nothing is destroyed - its readings and the
// measurements they fed stay, and restoreSensor brings it back as it was.
async function retireSensor(sensorId: number) {
  const response = await client.delete<UpdateSensorResponse>(`/api/sensors/${sensorId}`);
  const { data } = response;
  return data;
}

async function restoreSensor(sensorId: number) {
  const response = await client.post<UpdateSensorResponse>(`/api/sensors/${sensorId}/restore`);
  const { data } = response;
  return data;
}

async function createMeterMapping(request: CreateMeterMappingRequest) {
  const response = await client.post<MeterMappingResponse>('/api/sensors/meter_mappings', request);
  const { data } = response;
  return data;
}

async function deleteMeterMapping(mappingId: number) {
  await client.delete(`/api/sensors/meter_mappings/${mappingId}`);
}

export const sensorsApi = {
  getMappedSensors,
  getSensorDiagnostics,
  getAccountCredential,
  regenerateAccountCredential,
  getSensorsMap,
  getSensorMessages,
  getMessageRawBody,
  getSensorMessagesCount,
  createFormMapping,
  updateSensor,
  retireSensor,
  restoreSensor,
  createMeterMapping,
  deleteMeterMapping,
};
